import React, { useRef, useEffect, useState, lazy, Suspense } from "react";
import "./App.css";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import Navbar from "./components/layout/Navbar";
import Footer from "./components/layout/Footer";
import HeroSection from "./components/sections/HeroSection";
import ServicesGrid from "./components/sections/ServicesGrid";
import TechMarquee from "./components/sections/TechMarquee";
import StatsSection from "./components/sections/StatsSection";
import ProcessSection from "./components/sections/ProcessSection";
import CaseStudies from "./components/sections/CaseStudies";
import Testimonials from "./components/sections/Testimonials";
import CTABanner from "./components/sections/CTABanner";
import AdminPanel from "./components/AdminPanel";
import { Toaster } from "./components/ui/sonner";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import Login from "./components/Login";
import useMediaQuery from "./hooks/useMediaQuery";
import useSmoothScroll from "./hooks/useSmoothScroll";

// Lazy-load the heavy 3D scene
const Scene = lazy(() => import("./components/canvas/Scene"));

// Loading screen while 3D assets load
function Loader() {
  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 0, background: '#050510', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ width: 12, height: 12, borderRadius: '50%', background: '#00D4FF', opacity: 0.6, animation: 'pulse 1.5s ease-in-out infinite' }} />
      <style>{`@keyframes pulse { 0%, 100% { transform: scale(1); opacity: 0.4; } 50% { transform: scale(1.5); opacity: 1; } }`}</style>
    </div>
  );
}

// Mobile CSS-only animated background fallback
function MobileBackground() {
  return <div className="mobile-3d-fallback" />;
}

function LandingPage() {
  const isMobile = useMediaQuery("(max-width: 767px)");
  const scrollProgress = useRef(0);
  const mouse = useRef({ x: 0, y: 0 });

  // Track scroll progress
  useEffect(() => {
    const onScroll = () => {
      const total = document.documentElement.scrollHeight - window.innerHeight;
      scrollProgress.current = total > 0 ? window.scrollY / total : 0;
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Track mouse position (desktop only)
  useEffect(() => {
    if (isMobile) return;
    const onMove = (e) => {
      mouse.current = {
        x: (e.clientX / window.innerWidth) * 2 - 1,
        y: -(e.clientY / window.innerHeight) * 2 + 1,
      };
    };
    window.addEventListener("mousemove", onMove, { passive: true });
    return () => window.removeEventListener("mousemove", onMove);
  }, [isMobile]);

  // Smooth scroll via Lenis
  useSmoothScroll();

  return (
    <div className="min-h-screen" style={{ background: '#050510' }}>
      {/* Fixed 3D Canvas or Mobile Fallback */}
      {isMobile ? (
        <MobileBackground />
      ) : (
        <Suspense fallback={<Loader />}>
          <Scene scrollProgress={scrollProgress} mouse={mouse} />
        </Suspense>
      )}

      {/* Scrollable HTML overlay */}
      <div style={{ position: 'relative', zIndex: 10 }}>
        <Navbar />
        <main>
          <HeroSection />
          <TechMarquee />
          <ServicesGrid />
          <StatsSection />
          <ProcessSection />
          <CaseStudies />
          <Testimonials />
          <CTABanner />
        </main>
        <Footer />
      </div>
      <Toaster position="top-right" />
    </div>
  );
}

function AdminRoute() {
  const { isAuthenticated } = useAuth();
  if (!isAuthenticated) return <Login />;
  return <AdminPanel />;
}

function AppContent() {
  return (
    <div className="App">
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<LandingPage />} />
          <Route path="/admin" element={<AdminRoute />} />
        </Routes>
      </BrowserRouter>
    </div>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <AppContent />
    </AuthProvider>
  );
}
